import { create } from "zustand";
import { v4 as uuidv4 } from "uuid";
import { TempoElement } from "@/utils/tempoElement";
import { STORYBOARD_HYDRATION_STATUS } from "@/utils/constants";
import { track } from "@/utils/events/tracker";
import { filterSerializable } from "@/utils/utils";

export enum IFRAME_STATE {
  LOADING = "LOADING",
  SHOWING = "SHOWING",
  HIDDEN = "HIDDEN",
}

// Matches the interface in devtools
export enum FIXED_IFRAME_MESSAGE_IDS {
  HOVERED_ELEMENT_KEY = "HOVERED_ELEMENT_KEY",
  SELECTED_ELEMENT_KEY = "SELECTED_ELEMENT_KEY",
  MULTI_SELECTED_ELEMENT_KEYS = "MULTI_SELECTED_ELEMENT_KEYS",
  CONTEXT_REQUESTED = "CONTEXT_REQUESTED",
  WHEEL_EVENT = "WHEEL_EVENT",
  NAV_TREE = "NAV_TREE",
  CSS_EVALS_FOR_ELEMENT = "CSS_EVALS_FOR_ELEMENT",
  ELEMENT_CLASS_LIST = "ELEMENT_CLASS_LIST",
  KEY_DOWN_EVENT = "KEY_DOWN_EVENT",
  KEY_UP_EVENT = "KEY_UP_EVENT",
  MOUSE_MOVE_EVENT = "MOUSE_MOVE_EVENT",
  DRAG_START_EVENT = "DRAG_START_EVENT",
  DRAG_END_EVENT = "DRAG_END_EVENT",
  DRAG_CANCEL_EVENT = "DRAG_CANCEL_EVENT",
  LATEST_HREF = "LATEST_HREF",
  LATEST_HYDRATION_ERROR_STATUS = "LATEST_HYDRATION_ERROR_STATUS",
  START_EDITING_TEXT = "START_EDITING_TEXT",
  EDITED_TEXT = "EDITED_TEXT",
  INSTANT_UPDATE_DONE = "INSTANT_UPDATE_DONE",
  EDIT_DYNAMIC_TEXT = "EDIT_DYNAMIC_TEXT",
  ERROR = "ERROR",
}

const FIXED_IFRAME_MESSAGE_ID_VALUES = new Set(
  Object.values(FIXED_IFRAME_MESSAGE_IDS)
);

export interface IFrameMessage {
  id: string;
  type: string;
  payload: any;
}

interface IframeStore {
  pendingSelectionCodebaseId: string | null;
  setPendingSelectionCodebaseId: (codebaseId: string | null) => void;

  iframeMessagingPorts: { [storyboardId: string]: MessagePort };
  setIframeMessagingPorts: (iframeMessagingPorts: {
    [storyboardId: string]: MessagePort;
  }) => void;
  setPortForStoryboardId: (storyboardId: string, port: MessagePort) => void;

  // Actions
  sendMessageToIFrame: (
    message: Partial<IFrameMessage>,
    storyboardId?: string
  ) => void;
}

export const useIframeStore = create<IframeStore>((set, get) => ({
  pendingSelectionCodebaseId: null,
  setPendingSelectionCodebaseId: (codebaseId: string | null) => {
    set({ pendingSelectionCodebaseId: codebaseId });
  },

  iframeMessagingPorts: {},
  setIframeMessagingPorts: (iframeMessagingPorts: {
    [storyboardId: string]: MessagePort;
  }) => {
    set({ iframeMessagingPorts });
  },

  setPortForStoryboardId: (storyboardId: string, port: MessagePort) => {
    set((state) => ({
      iframeMessagingPorts: {
        ...state.iframeMessagingPorts,
        [storyboardId]: port,
      },
    }));
  },
  sendMessageToIFrame: ({ id, type, payload }, storyboardId) => {
    const newMessage = {
      id: id || uuidv4(),
      type: type!,
      payload: payload,
      storyboardId: storyboardId,
    };

    const { iframeMessagingPorts } = get();

    Object.keys(iframeMessagingPorts).forEach((sId) => {
      if (storyboardId && sId != storyboardId) {
        return;
      }

      const port = iframeMessagingPorts[sId];
      port.postMessage(filterSerializable(newMessage));
    });
  },
}));
